/**
 * The alert type names this console renders, and where they come from.
 *
 * **The service declares them, this console does not.** Every name here is the `name` on
 * `GET /alert-types`, which the alerting service builds from the detectors themselves. A
 * table kept in the console would drift the moment a detector shipped, and it would drift
 * in the direction that hides things: a page rendering a stale label is showing a name the
 * service has stopped agreeing with, and a picker built from a stale list simply never
 * offers the new type.
 *
 * <h2>Why the names live in a module-level map</h2>
 *
 * `alertTypeLabel` is called from templates, from free functions like `muteScope`, and from
 * four dialogs. Threading an injected service through all of them would put a constructor
 * parameter on things that have no other reason to have one, and would leave every caller
 * able to forget it.
 *
 * A process-wide map is the honest model rather than a shortcut, because **the answer is
 * global to the deployment**: `/alert-types` takes a `networkId` for authorization only —
 * detectors are not per network, and the spec says so. So a name learned while looking at
 * one network is correct while looking at any other, and there is nothing to scope.
 *
 * <h2>What a caller gets before the load lands</h2>
 *
 * The derived name — see {@link deriveAlertTypeLabel}. `ziti.router.flapping` reads as
 * "Ziti router flapping" until the registry answers and replaces it with whatever the
 * detector calls it. That is deliberately not blank and not the raw id: a column that
 * flickered from empty to a name would be worse than one that sharpens.
 *
 * An unknown type keeps the derived name for ever, which is the right answer for a detector
 * this deployment has but this listing has not asked about.
 */

import { Inject, Injectable } from '@angular/core';
import { AlertTypeView, ComponentType, OfflineRole, OfflineRungs } from '@netfoundry-ui/shared/model';
import { isEmpty, isNil } from 'lodash';
import { AlertServiceV2 } from './alert.service';

/**
 * Alert type id to display name, as the detectors declare them.
 *
 * Module level because the answer is deployment-global — see the class note. Written only
 * by {@link AlertTypeRegistry.load}, and read by {@link alertTypeLabel}.
 */
const NAMES = new Map<string, string>();

/**
 * A name derived from a dotted alert type id, for a type the registry has not got.
 *
 * Sentence case, and it costs an acronym its capitals. That is acceptable for a fallback:
 * it is what a reader sees before the registry answers, and it is replaced the moment it
 * does.
 *
 * Never the raw id. `service.dial-latency` in a column of names reads as a bug, and a
 * blank cell reads as an alert with no type.
 */
export function deriveAlertTypeLabel(alertType: string): string {
    if (!alertType) {
        return '';
    }
    const words = alertType
        .replace(/[._-]+/g, ' ')
        .trim()
        .toLowerCase();
    return words.charAt(0).toUpperCase() + words.slice(1);
}

/**
 * The name to show for an alert type: the one the detector declares, or one derived from
 * the id until the registry has it.
 *
 * A free function rather than a method, because it is called from templates, from the mute
 * sentences and from four dialogs — none of which would otherwise need a service injected.
 * {@link AlertTypeRegistry} is what fills the map it reads.
 */
export function alertTypeLabel(alertType: string): string {
    if (!alertType) {
        return '';
    }
    return NAMES.get(alertType) || deriveAlertTypeLabel(alertType);
}

/** The order offline kinds are shown in. A type not named here follows, in the order it is served. */
const OFFLINE_TYPE_ORDER: ComponentType[] = ['EDGE_ROUTER', 'IDENTITY'];

function offlineTypeRank(componentType: ComponentType): number {
    const rank = OFFLINE_TYPE_ORDER.indexOf(componentType);
    return rank < 0 ? OFFLINE_TYPE_ORDER.length : rank;
}

/**
 * What an offline policy is keyed on: a component type, and the role for a type graded per role.
 * Each kind has its own fallback ladder and its own answer to whether the rule runs where no policy says.
 */
export interface OfflineKind {
    componentType: ComponentType;
    role?: OfflineRole;
    enabledByDefault: boolean;
    fleetDefaults?: OfflineRungs;
}

/** One string per kind, for keying a map or a row. */
export function offlineKindKey(componentType: ComponentType, role?: OfflineRole | null): string {
    return isNil(role) ? componentType : `${componentType}/${role}`;
}

/**
 * Loads the alert types once and keeps them, so every page shows the same names.
 *
 * <strong>One request per session, not one per page.</strong> Three pages, a drawer and
 * four dialogs render alert type names; the answer is the same for all of them and does not
 * change while somebody is looking at it. {@link load} therefore answers from what it has,
 * and concurrent callers share the one request in flight rather than starting four.
 *
 * <strong>A failure is not an error here.</strong> Reading alert types needs `read` on the
 * `alert` resource type, and a caller without it still has pages to draw — they simply draw
 * derived names. So `load` never rejects: it resolves with what it has, which may be
 * nothing, and the pages carry on.
 */
@Injectable({ providedIn: 'root' })
export class AlertTypeRegistry {
    /** Everything the last successful load returned, ordered by alert type. */
    types: AlertTypeView[] = [];

    /** The one request in flight, so four callers on one page do not make four. */
    private inFlight?: Promise<AlertTypeView[]>;

    /** Whether a load has ever succeeded. A failed one is retried; a successful one is not. */
    private loaded = false;

    constructor(@Inject(AlertServiceV2) private alertService: AlertServiceV2) {}

    /**
     * The types a latency policy means anything for.
     *
     * **Not every type.** A policy written against a type whose `latencyPolicy` is false is
     * stored, served back, and never read by any detector — so offering thresholds for one
     * would be offering a setting that silently does nothing. This is the list a threshold
     * picker is built from.
     */
    get latencyPolicyTypes(): AlertTypeView[] {
        return this.types.filter((type) => type.latencyPolicy === true);
    }

    /** The types an offline policy grades. Absent `offlinePolicy` is not true, as for {@link latencyPolicyTypes}. */
    get offlinePolicyTypes(): AlertTypeView[] {
        return this.types.filter((type) => type.offlinePolicy === true && !isNil(type.componentType));
    }

    /** The component types an offline policy can be written for, edge routers first and then identities, without repeats. */
    get offlineComponentTypes(): ComponentType[] {
        const types = [...new Set(this.offlinePolicyTypes.map((type) => type.componentType as ComponentType))];
        return types.sort((a, b) => offlineTypeRank(a) - offlineTypeRank(b));
    }

    /**
     * Every kind an offline policy can be written for, edge routers first and then identities, without repeats.
     * A type with `offlineRoles` gives one kind per role; any other type gives one kind, on by default.
     */
    get offlineKinds(): OfflineKind[] {
        const kinds = new Map<string, OfflineKind>();
        for (const type of this.offlinePolicyTypes) {
            const componentType = type.componentType as ComponentType;
            const roles = type.offlineRoles || [];
            const found: OfflineKind[] =
                roles.length === 0
                    ? [{ componentType, enabledByDefault: true, fleetDefaults: type.offlineFleetDefaults || undefined }]
                    : roles.map((role) => ({
                          componentType,
                          role: role.role,
                          enabledByDefault: role.enabledByDefault === true,
                          fleetDefaults: role.fleetDefaults || undefined,
                      }));
            for (const kind of found) {
                const key = offlineKindKey(kind.componentType, kind.role);
                const known = kinds.get(key);
                if (!known) {
                    kinds.set(key, kind);
                } else if (isNil(known.fleetDefaults) && !isNil(kind.fleetDefaults)) {
                    kinds.set(key, { ...known, fleetDefaults: kind.fleetDefaults });
                }
            }
        }
        return [...kinds.values()].sort((a, b) => offlineTypeRank(a.componentType) - offlineTypeRank(b.componentType));
    }

    /** Whether the names are the detectors' own yet, or still derived from the ids. */
    get isLoaded(): boolean {
        return this.loaded;
    }

    /** One offline kind, or `undefined` when the service offers none for that type and role. */
    offlineKind(componentType?: ComponentType | null, role?: OfflineRole | null): OfflineKind | undefined {
        if (isNil(componentType)) {
            return undefined;
        }
        const key = offlineKindKey(componentType, role);
        return this.offlineKinds.find((kind) => offlineKindKey(kind.componentType, kind.role) === key);
    }

    /** The roles an offline policy for this type must name one of. Empty for a type graded on one ladder. */
    offlineRoles(componentType?: ComponentType | null): OfflineRole[] {
        return this.offlineKinds
            .filter((kind) => kind.componentType === componentType && !isNil(kind.role))
            .map((kind) => kind.role as OfflineRole);
    }

    /**
     * Whether the offline rule runs for a kind where no policy says. A kind the registry does not know is read as the
     * service ships it: on for a type without roles, off for a role.
     */
    offlineEnabledByDefault(componentType?: ComponentType | null, role?: OfflineRole | null): boolean {
        return this.offlineKind(componentType, role)?.enabledByDefault ?? isNil(role);
    }

    /**
     * Reads the alert types, once.
     *
     * Answers from what it has after the first success, shares the request in flight
     * otherwise, and never rejects — see the class note. A network is needed only for
     * authorization; the answer does not depend on which one.
     */
    load(networkId: string): Promise<AlertTypeView[]> {
        if (this.loaded || isEmpty(networkId)) {
            return Promise.resolve(this.types);
        }
        if (this.inFlight) {
            return this.inFlight;
        }
        this.inFlight = this.alertService
            .listAlertTypes(networkId)
            .then((types) => {
                this.types = types;
                this.loaded = true;
                for (const type of types) {
                    if (type.alertType && !isEmpty(type.name)) {
                        NAMES.set(type.alertType, type.name as string);
                    }
                }
                return this.types;
            })
            /* Reading alert types is a separate privilege from reading alerts, so a 403
             * here is the expected answer for somebody who can work the pages perfectly
             * well. They get derived names, and nothing fails. `loaded` stays false so a
             * later page may try again. */
            .catch(() => this.types)
            .finally(() => {
                this.inFlight = undefined;
            });
        return this.inFlight;
    }

    /** The fleet ladder for one kind, or `undefined` when the service serves none for it. */
    offlineFleetDefaults(componentType?: ComponentType | null, role?: OfflineRole | null): OfflineRungs | undefined {
        return this.offlineKind(componentType, role)?.fleetDefaults;
    }

    /** The name to show for one alert type. The free {@link alertTypeLabel} is the same call. */
    label(alertType: string): string {
        return alertTypeLabel(alertType);
    }

    /**
     * Empties the registry, for a test that needs the next load to happen.
     *
     * The map is module level and outlives a TestBed, so a test that seeded it would
     * otherwise leak names into the next one.
     */
    reset(): void {
        NAMES.clear();
        this.types = [];
        this.loaded = false;
        this.inFlight = undefined;
    }
}
